import React from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { getCurrentCustomer } from "../lib/customerSession";

/**
 * Yellow banner shown on the executive dashboard that surfaces how complete
 * the post-signup wizard is, with a CTA to finish the remaining steps.
 * Hidden for customers who have already completed the wizard.
 */
export default function ProfileCompletionBanner({ customerData, isLoading }) {
  const navigate = useNavigate();

  // Hide while the initial dashboard-data fetch is in-flight; otherwise the
  // banner briefly flashes "Profile 0% complete" before the real percentage
  // (often 100% / setup_complete=true) arrives. (Feb 2026 fix.)
  if (isLoading) return null;
  if (!customerData) return null;

  const customer = (() => {
    try { return getCurrentCustomer(); } catch (_e) { return null; }
  })();
  if (!customer) return null;

  const params = customerData?.params || {};
  const pct    = Number.isFinite(Number(params.setup_completion_pct))
    ? Math.max(0, Math.min(100, Number(params.setup_completion_pct)))
    : 0;
  const complete = !!params.setup_complete || pct >= 100;
  if (complete) return null;

  return (
    <div
      data-testid="profile-completion-banner"
      className="flex items-center justify-between gap-3 flex-wrap px-6 py-2.5 border-b border-status-drifting-border bg-status-drifting-bg"
    >
      <div className="flex items-center gap-2 text-[13px] text-ink-700">
        <ListChecks size={15} className="text-status-drifting-text" />
        <span>
          <b>Profile {pct}% complete.</b> Finish the setup wizard to unlock the full dashboard intelligence.
        </span>
        <div
          data-testid="profile-completion-bar"
          className="ml-1.5 h-1.5 w-[140px] overflow-hidden rounded-full bg-status-drifting-border"
        >
          <div className="h-full bg-status-drifting" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <button
        data-testid="profile-completion-cta"
        onClick={() => navigate("/setup")}
        className="rounded-md bg-status-drifting px-3.5 py-1.5 text-[12px] font-semibold text-white hover:bg-status-drifting-text transition-colors"
      >
        Continue setup →
      </button>
    </div>
  );
}
